import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {TranslocoService} from '@ngneat/transloco';
import {AbstractComponent} from "../abstract.component";
import {
  AmountStatus,
  ApplicationFormSettings,
  CategoryItem,
  Competitor,
  CompetitorFilter,
  CompetitorStatus,
  ControlPointTime,
  Country,
  CustomFieldValue,
  DEFAULT_PAGE_INDEX,
  DEFAULT_PAGE_SIZE,
  EventItem,
  FieldConfig,
  Lookups,
  PagedFilter,
  Pages,
  Paging,
  Race,
  RaceAdditionalPricingItem,
  RaceCommercialItem, RaceRegistration,
  RaceSettings,
  RegistrationDisplayFlags,
  TeamType,
  VisibilityTypes,
} from "@model";
import {getLanguageValue, Sort} from "@utility";
import {CompetitorService, CompetitorTableCacheService, LoaderService, RaceCommercialService} from "@services";
import {ActivatedRoute} from '@angular/router';
import {
  OnEditCompetitor
} from "../competitor-table-dropdown-actions-row/competitor-table-dropdown-actions-row.component";
import {BehaviorSubject, forkJoin, of} from "rxjs";
import {debounceTime, map, switchMap} from 'rxjs/operators';

@Component({
  selector: 'app-competitor-table',
  templateUrl: './competitor-table.component.html',
  styleUrls: ['./competitor-table.component.scss']
})
export class CompetitorTableComponent extends AbstractComponent implements OnInit, Paging<Competitor> {
  page: number = DEFAULT_PAGE_INDEX;
  hasMore: boolean = false;
  isLoading: boolean = false;

  @Input() competitors: Competitor[] = [];
  @Input() race: Race | undefined;
  @Input() event: EventItem | undefined;
  @Input() isAdmin: boolean = false;
  @Input() isTeamMembersManagement: boolean = false;
  @Input() showLoadMoreButton: boolean = false;
  @Input() showBorderBottom: boolean = true;
  @Input() categories: CategoryItem[] = [];
  @Input({required: true}) languages: Lookups[] = [];
  @Input({required: true}) countries: Country[] = [];
  @Input({required: true}) genders: Lookups[] = [];
  @Input() filteringEnabled: boolean = true;
  @Input() raceFilteringEnabled: boolean = false;
  @Input() showEmptyState: boolean = true;
  @Input() showHeader: boolean = false;
  @Input() raceSettings: RaceSettings | undefined;
  @Input() raceSettingsList: RaceSettings[] = [];
  @Input() applicationFormSettingsList: ApplicationFormSettings[] = []
  @Input() applicationFormSettings: ApplicationFormSettings | undefined;
  @Input() raceAdditionalPricingItems: RaceAdditionalPricingItem [] = [];
  @Input() raceCommercialItems: RaceCommercialItem[] = [];
  @Input() measurements: boolean = false;
  @Input() allowOnlyRaceView: boolean = false;
  @Input() tableMode: CompetitorTableType = CompetitorTableType.RACE_VIEW;
  @Output() filterChange: EventEmitter<PagedFilter<CompetitorFilter>> = new EventEmitter<PagedFilter<CompetitorFilter>>();
  @Output() competitorListChanged: EventEmitter<Competitor[]> = new EventEmitter<Competitor[]>();
  @Output() onEditTeamMemberButtonClick: EventEmitter<OnEditCompetitor> = new EventEmitter<OnEditCompetitor>();
  @Output() chronometarRaceCompetitorStartStop = new EventEmitter<Race>();
  @Output() refreshCounters = new EventEmitter<void>();

  private latestFetch$ = new BehaviorSubject<{ loadMore: boolean } | null>(null);

  name: string | undefined = "";
  gender: number | undefined = undefined;
  category: CategoryItem | undefined = undefined;
  selectedStatus: CompetitorStatus | undefined;
  raceId: number | undefined = undefined;
  raceIds: number[] = [];
  raceNames: string[] = [];

  competitorStatuses: CompetitorStatus[] = [CompetitorStatus.REG, CompetitorStatus.BIB, CompetitorStatus.RDY,
    CompetitorStatus.STA, CompetitorStatus.FIN, CompetitorStatus.DSQ, CompetitorStatus.DNF, CompetitorStatus.TLE, CompetitorStatus.DNS];

  allowedVisibilityTypes: VisibilityTypes[] = [VisibilityTypes.ALWAYS_VISIBLE, VisibilityTypes.VISIBLE_TO_PUBLIC]

  competitorTableData: CompetitorTableRowData[] = [];

  competitorTableAvailableColumns: Set<keyof CompetitorTableRowData> = new Set<keyof CompetitorTableRowData>();
  competitorTableSelectedDefaultColumns: Set<keyof CompetitorTableRowData> = new Set<keyof CompetitorTableRowData>();
  competitorTableAvailableCustomColumns: FieldConfig[] = [];
  competitorTableSelectedCustomColumns: FieldConfig[] = [];

  competitorTableAvailableAdditionalPricingColumns: RaceAdditionalPricingItem[] = [];
  competitorTableSelectedAdditionalPricingColumns: RaceAdditionalPricingItem[] = [];

  competitorTableSelectedColumns: (keyof CompetitorTableRowData | string)[] = [];
  competitorTableMobileColumns: Map<keyof CompetitorTableRowData, boolean> = new Map<keyof CompetitorTableRowData, boolean>();

  options: Map<string, string> = new Map<string, string>();
  filteringOptions: Map<(keyof Competitor), boolean> = new Map<(keyof Competitor), boolean>();
  protected readonly CompetitorTableType = CompetitorTableType;
  protected readonly AmountStatus = AmountStatus;
  protected readonly TeamType = TeamType;
  public static readonly CUSTOM_FIELD_COLUMN_PREFIX = "custom_field_";
  public static readonly ADDITIONAL_PRICING_ITEM_COLUMN_PREFIX = "additional_pricing_item_";

  constructor(private translocoService: TranslocoService, private competitorService: CompetitorService, private raceCommercialService: RaceCommercialService,
              private loaderService: LoaderService, private route: ActivatedRoute, private competitorTableCache: CompetitorTableCacheService) {
    super();
  }

  ngOnInit(): void {
    this.setupLatestRequestHandler();
    this.init();
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['competitors'] && !changes['competitors'].firstChange) {
      /// re init component if races have been started or stopped
      this.name = "";
      this.raceId = undefined;
      this.selectedStatus = undefined;
      this.gender = undefined;
      this.category = undefined;
      this.page = 1;
      this.init();
    }
  }

  private setupLatestRequestHandler() {
    this.subscriptions.push(this.latestFetch$
    .pipe(
      debounceTime(400),
      switchMap((request: { loadMore: boolean } | null) => {
          if (!request) {
            return of(null)
          }
          if (request.loadMore) {
            this.loaderService.startLoading();
          }
          this.isLoading = true;
          return this.competitorService.findCompetitors(
            new PagedFilter(new Pages(undefined, this.page), this.getFilter()), this.isAdmin, this.measurements).pipe(
            map(value => ({value, loadMore: request.loadMore})) // Pass along loadMore flag
          )
        }
      )
    )
    .subscribe(
      (response: { value: any, loadMore: boolean } | null) => {
        this.isLoading = false
        this.loaderService.dismissLoading()
        if (response) {
          const {value, loadMore} = response;
          this.handleExploreResult(value, loadMore);
        }
      },
      (_) => {
        console.log("Error fetching competitors");
        this.isLoading = false;
        this.loaderService.dismissLoading();
      }
    ));
  }

  private init() {
    if (this.event?.racesList?.length === 1 && !this.race) {
      this.race = this.event.racesList.at(0);
      this.allowOnlyRaceView = true;
    }
    this.raceSettingsList = this.raceSettingsList || [];
    this.applicationFormSettingsList = this.applicationFormSettingsList || [];
    this.handleExploreResult(this.competitors);
    if (this.isAdmin) {
      this.allowedVisibilityTypes = [VisibilityTypes.ALWAYS_VISIBLE, VisibilityTypes.VISIBLE_TO_ADMINISTRATION]
      if (!this.measurements) {
        this.competitorStatuses = [CompetitorStatus.RFP, ...this.competitorStatuses]
      }
    }
    const mobileFields: (keyof CompetitorTableRowData)[] = ["startNumber", "fullName", "teamName", "sex", "countryShort", "club", "paymentStatus", "categoryLabel"];
    mobileFields.forEach(field => {
      this.competitorTableMobileColumns.set(field, false);
    })
    this.setFilteringOptions();
    this.initRaceIds();
    this.initRaceNames();
    this.competitorTableData = [...this.initCompetitorTableData(this.competitors)];
    this.initCompetitorTableAvailableColumns();
    if (this.race) {
      this.handleRaceSelection(this.race.id, true)
    }
  }

  addCompetitorTableAvailableColumnsBasedOnRaceSettings(raceSettings: RaceSettings) {
    if (raceSettings.teamType && raceSettings.teamType !== TeamType.SINGLE && this.tableMode !== CompetitorTableType.TEAM_MEMBER_VIEW) {
      this.competitorTableAvailableColumns.add("teamName");
      this.competitorTableSelectedDefaultColumns.add("teamName")
      this.competitorTableMobileColumns.set("teamName", true);
    } else {
      this.competitorTableAvailableColumns.add("fullName");
      this.competitorTableSelectedDefaultColumns.add("fullName");
      this.competitorTableMobileColumns.set("fullName", true);
    }
    if (raceSettings.blockStart && !(this.tableMode === CompetitorTableType.TEAM_MEMBER_VIEW)) {
      this.competitorTableAvailableColumns.add("startBlock");
      this.competitorTableSelectedDefaultColumns.add("startBlock")
    }
  }

  addCompetitorTablePriceFields(additionalPricingItems: RaceAdditionalPricingItem[]) {
    if (this.isAdmin) {
      this.competitorTableAvailableAdditionalPricingColumns = [];
      this.competitorTableSelectedAdditionalPricingColumns = [];
      if ((this.raceSettings?.teamType === TeamType.SINGLE && this.tableMode === CompetitorTableType.RACE_VIEW)
          || ((this.raceSettings?.teamType && this.raceSettings?.teamType !== TeamType.SINGLE) && this.tableMode === CompetitorTableType.TEAM_MEMBER_VIEW)) {
        if (additionalPricingItems.length) {
          additionalPricingItems?.forEach((item: RaceAdditionalPricingItem) => {
            if (item.fieldVisibility !== VisibilityTypes.NOT_VISIBLE) {
              this.competitorTableAvailableAdditionalPricingColumns.push(item);
            }
          })
        }
      }
      this.options = this.getOptions();
    }
  }

  addCompetitorTableAvailableColumnsBasedOnApplicationFormSettings(applicationFormSettings: ApplicationFormSettings) {
    let fields: (keyof Competitor)[] = ["sex", "country", "categoryLabel", "club"];

    let adminFields: (keyof Competitor)[] = ["placeOfResidence", "address", "identityCardNumber", "identityCardExpires", "identityCardIssuer",
      "mobilePhone", "tshirt", "meal", "transport", "comment", "chipNumber", "licence", "emergencyPhone", "birthday", "nationality", "nickname"];

    if (this.tableMode !== CompetitorTableType.TEAM_MEMBER_VIEW) {
      adminFields.push("fee")
    }
    let teamMemberFields: (keyof Competitor)[] = ["discipline"];
    if (this.isAdmin) {
      fields = [...fields, ...adminFields]
    }

    for (const field of fields) {
      if (this.isApplicationFormFieldVisible(field, applicationFormSettings)) {
        this.competitorTableAvailableColumns.add(field as keyof CompetitorTableRowData);
        if (field === "sex") {
          this.competitorTableSelectedDefaultColumns.add("sex");
          this.competitorTableMobileColumns.set("sex", true)
        }
        if (field === "country" || field === "club" || field == "categoryLabel") {
          this.competitorTableMobileColumns.set(field, true);
        }
      }
    }

    if (this.tableMode === CompetitorTableType.RACE_VIEW || this.tableMode === CompetitorTableType.TEAM_MEMBER_VIEW) {
      applicationFormSettings.customFields?.forEach((customField: FieldConfig) => {
        if (this.isCustomFieldVisible(customField)) {
          this.competitorTableAvailableCustomColumns.push(customField);
        }
      })
    }

    if (this.tableMode === CompetitorTableType.TEAM_MEMBER_VIEW) {
      for (const field of teamMemberFields) {
        if (this.isApplicationFormFieldVisible(field, applicationFormSettings)) {
          this.competitorTableAvailableColumns.add(field as keyof CompetitorTableRowData);
        }
      }
    }

  }

  initCompetitorTableAvailableColumns() {
    this.competitorTableSelectedDefaultColumns = new Set<keyof CompetitorTableRowData>();
    this.competitorTableAvailableColumns = new Set<keyof CompetitorTableRowData>();
    this.competitorTableSelectedColumns = [];
    this.competitorTableSelectedCustomColumns = [];
    this.competitorTableAvailableCustomColumns = [];
    this.competitorTableAvailableAdditionalPricingColumns = [];
    this.competitorTableSelectedAdditionalPricingColumns = [];
    if (this.isAdmin) {
      this.competitorTableAvailableColumns.add("id");
      this.competitorTableSelectedDefaultColumns.add("id");

      this.competitorTableAvailableColumns.add("email");
      this.competitorTableSelectedDefaultColumns.add("email");

      this.competitorTableAvailableColumns.add("created")
      this.competitorTableAvailableColumns.add("modified");
      if (this.tableMode !== CompetitorTableType.TEAM_MEMBER_VIEW) {
        this.competitorTableAvailableColumns.add("status")
        this.competitorTableSelectedDefaultColumns.add("status")
        this.competitorTableAvailableColumns.add("registrationId")
        this.competitorTableSelectedDefaultColumns.add("registrationId")
      }

      if (this.measurements) {
        this.competitorTableAvailableColumns.add("startTime");
        this.competitorTableSelectedDefaultColumns.add("startTime");
      }
    }
    if (this.tableMode === CompetitorTableType.EVENT_VIEW) {
      if (this.raceSettingsList.length) {
        this.raceSettingsList.forEach((raceSettings: RaceSettings) => {
          this.addCompetitorTableAvailableColumnsBasedOnRaceSettings(raceSettings);
        })
      } else {
        this.competitorTableAvailableColumns.add("fullName");
        this.competitorTableSelectedDefaultColumns.add("fullName")
      }
      if (this.applicationFormSettingsList.length) {
        this.applicationFormSettingsList.forEach((applicationFormSettings: ApplicationFormSettings) => {
          this.addCompetitorTableAvailableColumnsBasedOnApplicationFormSettings(applicationFormSettings);
        })
      }
      this.competitorTableAvailableColumns.add("raceName");
      this.competitorTableSelectedDefaultColumns.add("raceName");
    } else if (this.tableMode === CompetitorTableType.RACE_VIEW || this.tableMode === CompetitorTableType.TEAM_MEMBER_VIEW) {
      if (this.raceSettings) {
        this.addCompetitorTableAvailableColumnsBasedOnRaceSettings(this.raceSettings);
      } else {
        this.competitorTableAvailableColumns.add("fullName");
        this.competitorTableSelectedDefaultColumns.add("fullName")
      }
      if (this.applicationFormSettings) {
        this.addCompetitorTableAvailableColumnsBasedOnApplicationFormSettings(this.applicationFormSettings);
      }
      this.addCompetitorTablePriceFields(this.raceAdditionalPricingItems);
    }

    if (!(this.tableMode === CompetitorTableType.TEAM_MEMBER_VIEW)) {
      this.competitorTableAvailableColumns.add("startNumber");
      this.competitorTableSelectedDefaultColumns.add("startNumber");
      this.competitorTableMobileColumns.set("startNumber", true);
      //for admin always, for public based on display from eventPayment settings
      if (this.isAdmin || this.event?.paymentSettings?.displayPaymentStatusForRegistrants) {
        this.competitorTableSelectedDefaultColumns.add("paymentStatus")
        this.competitorTableAvailableColumns.add("paymentStatus");
        this.competitorTableMobileColumns.set("paymentStatus", true);
      }
    }

    const defaultColumnsList = Array.from(this.competitorTableSelectedDefaultColumns);
    this.sortByDefault(defaultColumnsList);
    this.competitorTableSelectedDefaultColumns = new Set<keyof CompetitorTableRowData>(defaultColumnsList);
    this.competitorTableSelectedColumns = [...defaultColumnsList];

    const availableColumnsList = Array.from(this.competitorTableAvailableColumns);
    this.sortByDefault(availableColumnsList);
    this.competitorTableAvailableColumns = new Set<keyof CompetitorTableRowData>(availableColumnsList);
    this.options = this.getOptions();
    this.getColsFromCache();
  }

  getColsFromCache() {
    if (this.tableMode !== CompetitorTableType.TEAM_MEMBER_VIEW && this.isAdmin) {
      this.competitorTableCache
          .getCompetitorTableColsForEvent(this.event!.id!, this.raceId, this.measurements)
          .subscribe((result) => {
            const cols: (keyof CompetitorTableRowData | string)[]  = result.selectedCols && result.selectedCols.length
                ? result.selectedCols
                : [...this.competitorTableSelectedColumns];
            this.competitorTableSelectedColumns = cols;
            this.competitorTableSelectedCustomColumns = [];
            this.competitorTableSelectedAdditionalPricingColumns = [];
            result.selectedCustomColIds?.forEach(id => {
              const field = this.competitorTableAvailableCustomColumns.find(value => value.id === id)
              if (field) {
                this.competitorTableSelectedCustomColumns.push(field)
              }
            })
            result.selectedPricingItemIds?.forEach(id => {
              const field = this.competitorTableAvailableAdditionalPricingColumns.find(value => value.id === id)
              if (field) {
                this.competitorTableSelectedAdditionalPricingColumns.push(field)
              }
            })
          });
    }
  }

  competitorToCompetitorRowData(competitor: Competitor, race: Race | undefined, applicationFormSettings: ApplicationFormSettings | undefined, raceSettings: RaceSettings | undefined, isTeamMember: boolean = false): CompetitorTableRowData {
    const isTeamMemberView = this.tableMode === CompetitorTableType.TEAM_MEMBER_VIEW;
    const competitorRowData: CompetitorTableRowData = new CompetitorTableRowData();
    competitorRowData.id = this.getCompetitorRowDataFieldValue(competitor.id, this.isAdmin, this.isAdmin);
    competitorRowData.raceId = competitor.raceId;
    competitorRowData.registrationId = this.getCompetitorRowDataFieldValue(competitor.registrationId, this.isAdmin, this.isAdmin);
    const paymentStatusDisplay: boolean = !!(this.isAdmin || this.event?.paymentSettings?.displayPaymentStatusForRegistrants);
    competitorRowData.paymentStatus = this.getCompetitorRowDataFieldValue(RaceRegistration.getAmountStatus(competitor.registration), paymentStatusDisplay, paymentStatusDisplay);
    competitorRowData.fullName = this.getCompetitorRowDataFieldValue(((competitor?.firstName ?? "") + " " + (competitor?.lastName ?? "")), raceSettings?.teamType === TeamType.SINGLE || this.tableMode === CompetitorTableType.TEAM_MEMBER_VIEW || isTeamMember, raceSettings?.teamType === TeamType.SINGLE || this.tableMode === CompetitorTableType.TEAM_MEMBER_VIEW || isTeamMember);
    competitorRowData.teamName = this.getCompetitorRowDataFieldValue(competitor.lastName, !!raceSettings?.teamType && raceSettings?.teamType !== TeamType.SINGLE || isTeamMember, !!raceSettings?.teamType && raceSettings?.teamType !== TeamType.SINGLE || isTeamMember);
    competitorRowData.status = this.getCompetitorRowDataFieldValue(competitor.status ? competitor.status : "-", this.isAdmin, this.isAdmin);
    competitorRowData.startNumber = this.getCompetitorRowDataFieldValue(competitor.startNumber, !isTeamMemberView, !isTeamMemberView);
    competitorRowData.startBlock = this.getCompetitorRowDataFieldValue(competitor.startBlock, !isTeamMemberView && !!raceSettings?.blockStart, !isTeamMemberView && !!raceSettings?.blockStart);
    competitorRowData.created = this.getCompetitorRowDataFieldValue(competitor.created, this.isAdmin, this.isAdmin);
    competitorRowData.modified = this.getCompetitorRowDataFieldValue(competitor.modified, this.isAdmin, this.isAdmin);
    competitorRowData.email = this.getCompetitorRowDataFieldValue(competitor.email, true, true);
    competitorRowData.sex = this.getCompetitorRowDataFieldValue(
      competitor.sex != null ? String(competitor.sex) : undefined,
      this.isApplicationFormFieldUsed("sex", applicationFormSettings),
      this.isApplicationFormFieldVisible("sex", applicationFormSettings)
    );
    if (competitor.country) {
      const country = this.countries.find((c: Country) => c.id === competitor.country);
      if (country?.id) {
        competitorRowData.country = this.getCompetitorRowDataFieldValue(this.translocoService.translate("app.common.country." + country!.id), this.isApplicationFormFieldUsed("country", applicationFormSettings), this.isApplicationFormFieldVisible("country", applicationFormSettings));
        competitorRowData.countryShort = this.getCompetitorRowDataFieldValue(country.iocCode, this.isApplicationFormFieldUsed("country", applicationFormSettings), this.isApplicationFormFieldVisible("country", applicationFormSettings));
      } else {
        competitorRowData.country = this.getCompetitorRowDataFieldValue("-", this.isApplicationFormFieldUsed("country", applicationFormSettings), this.isApplicationFormFieldVisible("country", applicationFormSettings));
      }
    } else {
      competitorRowData.country = this.getCompetitorRowDataFieldValue("-", this.isApplicationFormFieldUsed("country", applicationFormSettings), this.isApplicationFormFieldVisible("country", applicationFormSettings));
    }
    competitorRowData.categoryLabel = this.getCompetitorRowDataFieldValue(competitor.categoryLabel, this.isApplicationFormFieldVisible("categoryLabel", applicationFormSettings), this.isApplicationFormFieldVisible("categoryLabel", applicationFormSettings));
    competitorRowData.placeOfResidence = this.getCompetitorRowDataFieldValue(competitor.placeOfResidence, this.isApplicationFormFieldUsed("placeOfResidence", applicationFormSettings), this.isApplicationFormFieldVisible("placeOfResidence", applicationFormSettings));
    competitorRowData.address = this.getCompetitorRowDataFieldValue(competitor.address, this.isApplicationFormFieldUsed("address", applicationFormSettings), this.isApplicationFormFieldVisible("address", applicationFormSettings));
    competitorRowData.identityCardNumber = this.getCompetitorRowDataFieldValue(competitor.identityCardNumber, this.isApplicationFormFieldUsed("identityCardNumber", applicationFormSettings), this.isApplicationFormFieldVisible("identityCardNumber", applicationFormSettings));
    competitorRowData.identityCardExpires = this.getCompetitorRowDataFieldValue(competitor.identityCardExpires, this.isApplicationFormFieldUsed("identityCardExpires", applicationFormSettings), this.isApplicationFormFieldVisible("identityCardExpires", applicationFormSettings));
    competitorRowData.identityCardIssuer = this.getCompetitorRowDataFieldValue(competitor.identityCardIssuer, this.isApplicationFormFieldUsed("identityCardIssuer", applicationFormSettings), this.isApplicationFormFieldVisible("identityCardIssuer", applicationFormSettings));
    competitorRowData.club = this.getCompetitorRowDataFieldValue(competitor.club, this.isApplicationFormFieldUsed("club", applicationFormSettings), this.isApplicationFormFieldVisible("club", applicationFormSettings));
    competitorRowData.mobilePhone = this.getCompetitorRowDataFieldValue(competitor.mobilePhone, this.isApplicationFormFieldUsed("mobilePhone", applicationFormSettings), this.isApplicationFormFieldVisible("mobilePhone", applicationFormSettings));
    competitorRowData.tshirt = this.getCompetitorRowDataFieldValue(competitor.tshirt ? this.translocoService.translate("app.common.tshirt." + competitor.tshirt) : '-', this.isApplicationFormFieldUsed("tshirt", applicationFormSettings), this.isApplicationFormFieldVisible("tshirt", applicationFormSettings));
    competitorRowData.meal = this.getCompetitorRowDataFieldValue(competitor.meal ? this.translocoService.translate("app.common.meal." + competitor.meal) : '-', this.isApplicationFormFieldUsed("meal", applicationFormSettings), this.isApplicationFormFieldVisible("meal", applicationFormSettings));
    competitorRowData.transport = this.getCompetitorRowDataFieldValue(competitor.transport ? this.translocoService.translate("app.common.transport." + competitor.transport) : '-', this.isApplicationFormFieldUsed("transport", applicationFormSettings), this.isApplicationFormFieldVisible("transport", applicationFormSettings));
    competitorRowData.comment = this.getCompetitorRowDataFieldValue(competitor.comment, this.isApplicationFormFieldUsed("comment", applicationFormSettings), this.isApplicationFormFieldVisible("comment", applicationFormSettings));
    competitorRowData.chipNumber = this.getCompetitorRowDataFieldValue(competitor.chipNumber, this.isApplicationFormFieldUsed("chipNumber", applicationFormSettings), this.isApplicationFormFieldVisible("chipNumber", applicationFormSettings));
    competitorRowData.licence = this.getCompetitorRowDataFieldValue(competitor.licence, this.isApplicationFormFieldUsed("licence", applicationFormSettings), this.isApplicationFormFieldVisible("licence", applicationFormSettings));
    competitorRowData.emergencyPhone = this.getCompetitorRowDataFieldValue(competitor.emergencyPhone, this.isApplicationFormFieldUsed("emergencyPhone", applicationFormSettings), this.isApplicationFormFieldVisible("emergencyPhone", applicationFormSettings));
    competitorRowData.raceCommercialItem = this.getCompetitorRowDataFieldValue(competitor.raceCommercialItem, true, this.isApplicationFormFieldVisible("fee", applicationFormSettings));
    competitorRowData.birthday = this.getCompetitorRowDataFieldValue(competitor.birthday, this.isApplicationFormFieldUsed("birthday", applicationFormSettings), this.isApplicationFormFieldVisible("birthday", applicationFormSettings))
    competitorRowData.discipline = this.getCompetitorRowDataFieldValue(competitor.discipline ? this.translocoService.translate("race.registration.settings.discipline." + competitor.discipline) : "-", this.isApplicationFormFieldUsed("discipline", applicationFormSettings), this.isApplicationFormFieldVisible("discipline", applicationFormSettings))
    competitorRowData.nationality = this.getCompetitorRowDataFieldValue(competitor.nationality, this.isApplicationFormFieldUsed("nationality", applicationFormSettings), this.isApplicationFormFieldVisible("nationality", applicationFormSettings))
    competitorRowData.raceName = race?.name;
    competitorRowData.raceNameEn = race?.nameEng;
    competitorRowData.raceNameIta = race?.nameIta;
    competitorRowData.raceNameSlo = race?.nameSlo;
    competitorRowData.raceNameSr = race?.nameSr;
    competitorRowData.raceNameBs = race?.nameBs;
    competitorRowData.startTime = this.getCompetitorRowDataFieldValue(competitor.startTime, this.isAdmin, this.isAdmin);
    if (this.tableMode === CompetitorTableType.RACE_VIEW || this.tableMode === CompetitorTableType.TEAM_MEMBER_VIEW) {
      competitorRowData.customFieldValues = competitor.customFieldValues ? [...competitor.customFieldValues] : [];
      competitorRowData.raceAdditionalItems = competitor.raceAdditionalPricingItems ? [...competitor.raceAdditionalPricingItems] : [];
    }
    competitorRowData.fee = competitor.fee;

    if (competitor.teamMembers?.length) {
      const teamMembersData: CompetitorTableRowData[] = [];
      competitor.teamMembers.forEach(teamMember => {
        teamMembersData.push(this.competitorToCompetitorRowData(teamMember, race, applicationFormSettings, raceSettings, true));
      });
      competitorRowData.teamMembers = teamMembersData;
    }
    return competitorRowData
  }

  initCompetitorTableData(competitors: Competitor[]): CompetitorTableRowData[] {
    let lastRaceId: number | undefined;
    let raceSettings: RaceSettings | undefined;
    let applicationFormSettings: ApplicationFormSettings | undefined;
    let competitorTableRowDataList: CompetitorTableRowData[] = [];
    let race: Race | undefined;
    competitors.forEach((competitor: Competitor) => {
      if (this.tableMode === CompetitorTableType.EVENT_VIEW) {
        if (lastRaceId !== competitor.raceId) {
          lastRaceId = competitor.raceId;
          if (this.applicationFormSettingsList.length > 0) {
            applicationFormSettings = this.applicationFormSettingsList?.find(a => a.raceId === competitor.raceId);
          }
          if (this.raceSettingsList.length > 0) {
            raceSettings = this.raceSettingsList?.find(r => r.raceId === competitor.raceId);
          }
          race = this.event?.racesList?.find(r => r.id === competitor.raceId);
        }
      } else if (this.tableMode === CompetitorTableType.RACE_VIEW || this.tableMode === CompetitorTableType.TEAM_MEMBER_VIEW) {
        raceSettings = this.raceSettings;
        applicationFormSettings = this.applicationFormSettings;
        race = this.race;
      }
      const competitorRowData = this.competitorToCompetitorRowData(competitor, race, applicationFormSettings, raceSettings);
      if (!raceSettings) {
        competitorRowData.fullName = this.getCompetitorRowDataFieldValue(((competitor?.firstName ?? "") + " " + (competitor?.lastName ?? "")), true, true);
      }
      competitorTableRowDataList.push(competitorRowData);
    });

    return competitorTableRowDataList;
  }

  sortCompetitors() {
    const competitors: Competitor [] = [];
    for (const rowData of this.competitorTableData) {
      const competitor: Competitor | undefined = this.competitors.find(value => value.id === rowData.id);
      if (competitor) competitors.push(competitor);
    }
    this.competitors = [...competitors];
  }

  onEditCompetitor(onEditCompetitor: OnEditCompetitor) {
    const {competitor, index} = onEditCompetitor;
    if (this.tableMode === CompetitorTableType.EVENT_VIEW) {
      const raceSettings: RaceSettings | undefined = this.raceSettingsList?.find(r => r.raceId === competitor.raceId);
      const applicationFormSettings: ApplicationFormSettings | undefined = this.applicationFormSettingsList?.find(a => a.raceId === competitor.raceId);
      const race: Race | undefined = this.event?.racesList?.find(r => r.id === competitor.raceId);
      this.competitorTableData[index] = this.competitorToCompetitorRowData(competitor, race, applicationFormSettings, raceSettings)
    } else if (this.tableMode === CompetitorTableType.RACE_VIEW || this.tableMode === CompetitorTableType.TEAM_MEMBER_VIEW) {
      this.competitorTableData[index] = this.competitorToCompetitorRowData(competitor, this.race, this.applicationFormSettings, this.raceSettings);
    }
    this.refreshCounters.emit();
  }

  getCompetitorRowDataFieldValue(value: any, isUsed: boolean, isVisible: boolean) {
    if (isUsed || isVisible) {
      return value
    } else {
      return undefined;
    }
  }

  initRaceIds() {
    if (this.event?.racesList?.length ?? 0 > 0) {
      this.raceIds = this.event!.racesList!.map((race: Race) => race.id!);
    }
  }

  initRaceNames() {
    if (this.event?.racesList?.length ?? 0 > 0) {
      this.raceNames = this.event!.racesList!.map((race: Race) => race.name!);
    }
  }

  onSelectedFieldsChange(selectedFields: (keyof CompetitorTableRowData | string)[]) {
    if (!(selectedFields.length > 0)) {
      this.competitorTableSelectedColumns = Array.from(this.competitorTableSelectedDefaultColumns);
      this.competitorTableSelectedCustomColumns = [];
      this.competitorTableSelectedAdditionalPricingColumns = [];
    } else {
      this.competitorTableSelectedColumns = [...selectedFields];
      this.competitorTableSelectedCustomColumns = [];
      this.competitorTableSelectedAdditionalPricingColumns = []
      for (const field of selectedFields) {
        if (field.startsWith(CompetitorTableComponent.CUSTOM_FIELD_COLUMN_PREFIX)) {
          const id = Number(field.split(CompetitorTableComponent.CUSTOM_FIELD_COLUMN_PREFIX).at(-1));
          const fieldConfig: FieldConfig | undefined = this.applicationFormSettings?.customFields?.find(customField => customField.id === id);
          if (fieldConfig) {
            this.competitorTableSelectedCustomColumns.push(fieldConfig);
          }
        } else if (field.startsWith(CompetitorTableComponent.ADDITIONAL_PRICING_ITEM_COLUMN_PREFIX)) {
          const id = Number(field.split(CompetitorTableComponent.ADDITIONAL_PRICING_ITEM_COLUMN_PREFIX).at(-1));
          const item: RaceAdditionalPricingItem | undefined = this.raceAdditionalPricingItems?.find(pricingItem => pricingItem.id === id);
          if (item) {
            this.competitorTableSelectedAdditionalPricingColumns.push(item);
          }
        }
      }
    }
    this.sortByDefault(this.competitorTableSelectedColumns);
    if (this.tableMode !== CompetitorTableType.TEAM_MEMBER_VIEW && this.isAdmin) {
      this.competitorTableCache.saveCompetitorTableColsForEvent(this.event!.id!, this.raceId, this.competitorTableSelectedColumns, this.competitorTableSelectedCustomColumns, this.competitorTableSelectedAdditionalPricingColumns, this.measurements)
    }
  }

  setFilteringOptions() {
    this.filteringOptions.set('sex', this.isApplicationFormFieldVisible('sex'));
    this.filteringOptions.set('category', this.isApplicationFormFieldVisible('categoryLabel'));
  }

  handleSearchInput(value: string): void {
    if (value === "") {
      this.name = undefined;
    } else {
      this.name = value;
    }
    if (this.name === undefined || this.name === "" || this.name.length >= 3) {
      this.onExplore(false);
    }
  }

  handleGenderSelection(value?: any): void {
    if (value === this.gender || !value) {
      this.gender = undefined;
    } else {
      this.gender = value;
    }
    this.onExplore(false);
  }

  handleStatusSelection(status?: CompetitorStatus): void {
    if (this.isAdmin) {
      this.selectedStatus = status;
      this.onExplore(false);
    }
  }

  handleRaceSelection(value?: any, preventLoading: boolean = false): void {
    if ((value === this.raceId || !value)) {
        if (!this.allowOnlyRaceView) {
          this.raceId = undefined;
          this.tableMode = CompetitorTableType.EVENT_VIEW;
          this.raceSettings = undefined;
          this.race = undefined;
          this.applicationFormSettings = undefined;
          this.initCompetitorTableAvailableColumns();
        } else {
            preventLoading = true;
        }
    } else {
      this.raceId = value;
      this.tableMode = CompetitorTableType.RACE_VIEW;
      this.raceSettings = this.raceSettings?.raceId !== this.raceId ? this.raceSettingsList?.find(r => r.raceId === this.raceId) : this.raceSettings;
      this.applicationFormSettings = this.applicationFormSettings?.raceId !== this.raceId ? this.applicationFormSettingsList?.find(a => a.raceId === this.raceId): this.applicationFormSettings;
      this.getCommercialItems();
      this.race = this.event?.racesList?.find(r => r.id === this.raceId);
      this.initCompetitorTableAvailableColumns();
    }
    this.gender = undefined;
    this.category = undefined;
    this.setFilteringOptions();
    if (!preventLoading) {
      this.onExplore(false);
    }
  }

  handleCategorySelection(value?: any): void {
    if (value === this.category || !value) {
      this.category = undefined;
    } else {
      this.category = value;
    }
    this.onExplore(false);
  }

  handleExploreResult(value: Competitor[], loadMore?: boolean): void {
    let competitorRowData = [];
    if (value) {
      if (value.length > DEFAULT_PAGE_SIZE) {
        value = (value.slice(0, DEFAULT_PAGE_SIZE))
        this.hasMore = true;
      } else {
        this.hasMore = false;
      }
      competitorRowData = this.initCompetitorTableData(value);
      if (loadMore && this.competitors) {
        value = this.competitors.concat(value)
        this.competitorTableData = this.competitorTableData.concat(competitorRowData);
      } else {
        this.competitorTableData = [...competitorRowData];
      }
    }
    this.setListOfItems(value);
  }

  getCommercialItems() {
    if (this.raceId) {
      forkJoin({
        commercialItems: this.raceCommercialService.findRaceCommercialItems(this.raceId!, true),
        additionalPricingItems: this.raceCommercialService.findRaceAdditionalPricingItems(this.raceId!, true)
      }).subscribe((result: {
        commercialItems: RaceCommercialItem[],
        additionalPricingItems: RaceAdditionalPricingItem[]
      }) => {
        this.raceCommercialItems = result.commercialItems;
        this.raceAdditionalPricingItems = result.additionalPricingItems;
        this.addCompetitorTablePriceFields(this.raceAdditionalPricingItems);
        this.getColsFromCache();
      });
    }
  }

  setListOfItems(value: Competitor[] | null) {
    if (value && value.length > 0) {
      this.competitors = value;
      this.page++;
    } else {
      this.competitors = [];
      this.competitorTableData = [];
    }
  }

  onExplore(loadMore?: boolean) {
    if (!loadMore) {
      this.page = 1
      loadMore = false;
    }
    this.latestFetch$.next({loadMore});
  }

  loadMore(): void {
    this.onExplore(true);
  }

  isApplicationFormFieldVisible(fieldName: keyof Competitor, applicationFormSettings?: ApplicationFormSettings | undefined): boolean {
    if (!applicationFormSettings) {
      applicationFormSettings = this.applicationFormSettings;
    }
    const visibilityKey: keyof ApplicationFormSettings | undefined = ApplicationFormVisibleCompetitorFields.get(fieldName)
    if (visibilityKey && applicationFormSettings) {
      const fieldVisibility: any = applicationFormSettings[visibilityKey]
      if (typeof fieldVisibility === "number") {
        return this.allowedVisibilityTypes.includes(fieldVisibility as VisibilityTypes);
      } else return false;
    } else {
      return false;
    }
  }

  isApplicationFormFieldUsed(fieldName: keyof Competitor, applicationFormSettings: ApplicationFormSettings | undefined): boolean {
    if (!applicationFormSettings) {
      return false;
    }
    const displayFlagKey: keyof ApplicationFormSettings | undefined = ApplicationFormDisplayCompetitorFields.get(fieldName)
    if (displayFlagKey && applicationFormSettings) {
      const fieldUsage: any = applicationFormSettings[displayFlagKey]
      if (typeof fieldUsage === "number") {
        return fieldUsage !== RegistrationDisplayFlags.NOT_USED;
      } else return false;
    } else {
      return false;
    }
  }

  isCustomFieldVisible(customField: FieldConfig): boolean {
    return !!customField.visibility && this.allowedVisibilityTypes.includes((customField.visibility));
  }

  onScroll(event: any): void {
    if (!this.showLoadMoreButton) {
      if (event.target.offsetHeight + event.target.scrollTop >= event.target.scrollHeight) {
        if (this.hasMore) {
          this.onExplore(true);
        }
      }
    }
  }

  getColumnId(item: FieldConfig | RaceAdditionalPricingItem, prefix: string) {
    return `${prefix}${item.id}`
  }

  private addOptionsToMap(
    options: (FieldConfig | RaceAdditionalPricingItem)[],
    prefix: string,
    optionsMap: Map<string, string>
  ): void {
    options.forEach((option: FieldConfig | RaceAdditionalPricingItem) => {
      if (option.id) {
        const label = getLanguageValue(
          option.name,
          option.nameEng,
          option.nameIta,
          option.nameSlo,
          option.nameSr,
          option.nameBs,
          this.translocoService.getActiveLang(),
          option.name ?? option.nameEng ?? option.nameIta ?? option.nameSlo ?? option.nameSr ?? option.nameBs
        );
        optionsMap.set(this.getColumnId(option, prefix), label ?? "");
      }
    });
  }

  getOptions(): Map<string, string> {
    const optionsMap: Map<string, string> = new Map<string, string>();
    this.competitorTableAvailableColumns.forEach(((option: (keyof CompetitorTableRowData)) => {
      optionsMap.set(option, this.translocoService.translate('competitor.table.' + option))
    }));

    if (this.tableMode === CompetitorTableType.TEAM_MEMBER_VIEW || this.tableMode === CompetitorTableType.RACE_VIEW) {
      this.addOptionsToMap(this.competitorTableAvailableCustomColumns, CompetitorTableComponent.CUSTOM_FIELD_COLUMN_PREFIX, optionsMap);
      this.addOptionsToMap(this.competitorTableAvailableAdditionalPricingColumns, CompetitorTableComponent.ADDITIONAL_PRICING_ITEM_COLUMN_PREFIX, optionsMap);
    }
    return optionsMap;
  }

  getRaceOptions(): Map<number, string> {
    const optionsMap: Map<number, string> = new Map<number, string>();
    this.event!.racesList!.map((race: Race): void => {
      optionsMap.set(race.id!, race.name!);
    });
    return optionsMap;
  }

  editTeamMemberButtonClick(value: OnEditCompetitor) {
    this.onEditTeamMemberButtonClick.emit(value);
  }

  onChronometarRaceCompetitorStartStop(race: Race) {
    this.chronometarRaceCompetitorStartStop.emit(race);
    this.refreshCounters.emit();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  assignClass(field: string): string {
    if (field === 'id') {
      return 'id-width';
    } else if (field === 'startBlock') {
      return 'start-block-width';
    } else if (field === 'startNumber') {
      return 'start-number-width';
    } else if (field === 'registrationId') {
      return 'id-width';
    } else if (field === 'email') {
      return 'email-width';
    } else if (field === 'created') {
      return 'created-width';
    } else if (field === 'modified') {
      return 'modified-width';
    } else if (field === 'birthday') {
      return 'birthday-width';
    } else if (field === 'fullName' || field === 'teamName') {
      return 'full-name-width';
    } else if (field === 'raceName') {
      return 'race-name-width';
    } else if (field === 'startTime') {
      return 'start-time-width';
    } else if (field === 'paymentStatus') {
      return 'payment-status-width'
    } else if (field === 'sex') {
      return 'gender-col-width'
    } else if (field === 'status') {
      return 'th-status';
    }
    return '';
  }

  private getFilter(): CompetitorFilter {
    let filter: CompetitorFilter = new CompetitorFilter();
    if (this.category?.id) {
      filter.categories = [this.category!.id];
    }
    filter.gender = this.gender ?? undefined;
    filter.name = this.name === '' ? undefined : this.name;
    filter.eventId = +this.route.snapshot.queryParams['eventId'];
    if (this.race) {
      filter.raceId = this.race.id;
    } else {
      filter.raceId = this.raceId;
    }

    if (this.selectedStatus && this.isAdmin) {
      filter.competitorStatuses = [this.selectedStatus];
    }
    this.filterChange.emit(new PagedFilter(new Pages(undefined, this.page), filter));
    return filter;
  }

  private sortByDefault(arr: (keyof CompetitorTableRowData | string)[]) {
    const sortingArr = [...CompetitorTableRowDataSortingArray];
    Sort.sortByOrder(arr, sortingArr);
  }

  onDeleteCompetitor() {
    this.onExplore(false);
    this.refreshCounters.emit();
  }

  getCompetitorName(competitor: any): string {
    return (
      getLanguageValue(
        competitor?.raceCommercialItem?.name,
        competitor?.raceCommercialItem?.nameEng,
        competitor?.raceCommercialItem?.nameIta,
        competitor?.raceCommercialItem?.nameSlo,
        competitor?.raceCommercialItem?.nameSr,
        competitor?.raceCommercialItem?.nameBs,
        this.translocoService.getActiveLang(),
        competitor?.raceCommercialItem?.name
      ) || '-'
    );
  }
}

export class CompetitorTableRowData {

  id?: number;
  registrationId?: number;
  startBlock?: number;
  startNumber?: string;
  fullName?: string;
  teamName?: string;
  email?: string;
  status?: CompetitorStatus;
  sex?: string;
  mobilePhone?: string;
  raceId?: number;
  raceName?: string;
  raceNameEn?: string;
  raceNameIta?: string;
  raceNameSlo?: string;
  raceNameSr?: string;
  raceNameBs?: string;
  paymentStatus?: number;
  country?: string;
  countryShort?: string;
  categoryLabel?: string;
  placeOfResidence?: string;
  address?: string;
  identityCardNumber?: string;
  identityCardExpires?: Date;
  identityCardIssuer?: string;
  club?: string;
  tshirt?: string;
  meal?: string;
  transport?: string;
  comment?: string;
  chipNumber?: string;
  licence?: string;
  emergencyPhone?: string;
  fee?: string;
  discipline?: string;
  nationality?: string;
  controlPointsTimes?: ControlPointTime[];
  startTime?: Date;
  birthday?: Date;
  created?: Date;
  modified?: Date;
  customFieldValues: CustomFieldValue[] = [];
  raceCommercialItem?: RaceCommercialItem;
  raceAdditionalItems?: RaceAdditionalPricingItem[];
  teamMembers?: CompetitorTableRowData[];
}

export const CompetitorTableRowDataSortingArray: (keyof CompetitorTableRowData)[] = [
  "id", "registrationId", "startBlock", "startNumber", "fullName", "teamName", "email", "status", "sex", "startTime", "mobilePhone", "paymentStatus",
  "raceName", "raceNameEn", "raceNameIta", "raceNameSlo", "raceNameSr", "raceNameBs", "birthday", "categoryLabel", "country",
  "placeOfResidence", "address", "club", "identityCardNumber", "identityCardExpires", "identityCardIssuer", "tshirt",
  "meal", "transport", "comment", "chipNumber", "licence", "emergencyPhone", "fee", "discipline", "nationality", "controlPointsTimes",
  "created", "modified"
]

export const ApplicationFormVisibleCompetitorFields: Map<keyof Competitor, keyof ApplicationFormSettings> = new Map([
  ["sex", "genderVisibility"],
  ["country", "countryVisibility"],
  ["categoryLabel", "categoryRegistrationVisibility"],
  ["placeOfResidence", "cityVisibility"],
  ["address", "addressVisibility"],
  ["identityCardNumber", "identityCardNumberVisibility"],
  ["identityCardExpires", "identityCardExpiresVisibility"],
  ["identityCardIssuer", "identityCardIssuerVisibility"],
  ["club", "clubVisibility"],
  ["mobilePhone", "mobilePhoneVisibility"],
  ["tshirt", "tshirtVisibility"],
  ["meal", "mealVisibility"],
  ["transport", "transportVisibility"],
  ["comment", "commentVisibility"],
  ["chipNumber", "chipNumberVisibility"],
  ["bestTime", "bestTimeVisibility"],
  ["licence", "licenceVisibility"],
  ["emergencyPhone", "emergencyPhoneVisibility"],
  ["fee", "feeVisibility"],
  ["birthday", "birthdayRegistrationDisplayFlag"],
  ["discipline", "disciplineVisibility"],
  ["nationality", "nationalityVisibility"]
]);

export const ApplicationFormDisplayCompetitorFields: Map<keyof Competitor, keyof ApplicationFormSettings> = new Map([
  ["sex", "genderRegistrationDisplayFlag"],
  ["country", "countryRegistrationDisplayFlag"],
  ["categoryLabel", "categoryRegistrationVisibility"],
  ["placeOfResidence", "cityRegistrationDisplayFlag"],
  ["address", "addressRegistrationDisplayFlag"],
  ["identityCardNumber", "identityCardNumberRegistrationDisplayFlag"],
  ["identityCardExpires", "identityCardExpiresRegistrationDisplayFlag"],
  ["identityCardIssuer", "identityCardIssuerRegistrationDisplayFlag"],
  ["club", "clubRegistrationDisplayFlag"],
  ["mobilePhone", "mobilePhoneRegistrationDisplayFlag"],
  ["tshirt", "tshirtRegistrationDisplayFlag"],
  ["meal", "mealRegistrationDisplayFlag"],
  ["transport", "transportRegistrationDisplayFlag"],
  ["comment", "commentRegistrationDisplayFlag"],
  ["chipNumber", "chipNumberRegistrationDisplayFlag"],
  ["bestTime", "bestTimeRegistrationDisplayFlag"],
  ["licence", "licenceRegistrationDisplayFlag"],
  ["emergencyPhone", "emergencyPhoneRegistrationDisplayFlag"],
  ["fee", "feeVisibility"],
  ["birthday", "birthdayRegistrationDisplayFlag"],
  ["discipline", "disciplineVisibility"],
  ["nationality", "nationalityRegistrationDisplayFlag"]
]);

export enum CompetitorTableType {
  EVENT_VIEW,
  RACE_VIEW,
  TEAM_MEMBER_VIEW
}

export enum CompetitorTableAction {
  START = 1,
  CANCEL_START = 2,
  SET_COMPETITOR_DNS = 3,
  SET_COMPETITOR_BIB = 4,
  SET_COMPETITOR_RDY = 5,
  SET_COMPETITOR_START_TIME = 6,
  EDIT_COMPETITOR = 7,
  DELETE_COMPETITOR = 8,
  ASSIGN_START_NUMBER = 9,
  CHANGE_COMPETITOR_RACE = 10,
  CONFIRM_REGISTRATION = 11,
  DELETE_COMPETITOR_TIMES = 12
}

